import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { HardDrive, AlertTriangle, RefreshCw, Database, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const LEVEL_STYLES = {
  ok: { ring: 'ring-emerald-300', bg: 'from-emerald-500 to-teal-600', text: 'text-emerald-700', label: 'OK', dot: 'bg-emerald-500' },
  elevated: { ring: 'ring-blue-300', bg: 'from-blue-500 to-sky-600', text: 'text-blue-700', label: 'Élevé', dot: 'bg-blue-500' },
  warning: { ring: 'ring-amber-300', bg: 'from-amber-500 to-orange-500', text: 'text-amber-800', label: 'Attention', dot: 'bg-amber-500' },
  critical: { ring: 'ring-red-300', bg: 'from-red-500 to-rose-600', text: 'text-red-700', label: 'Critique', dot: 'bg-red-500 animate-pulse' },
};

export default function DiskMonitoringWidget() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purgeStatus, setPurgeStatus] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [diskRes, schedRes] = await Promise.all([
        axios.get(`${API}/admin/pdf-cache/disk-status`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/pdf-cache/draft-purge/status`, { headers: getAuthHeader() }),
      ]);
      setStats(diskRes.data);
      setPurgeStatus(schedRes.data);
    } catch {
      setStats(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!stats) {
    return (
      <Card data-testid="disk-monitoring-widget" className="border-slate-200">
        <CardContent className="p-6 flex items-center justify-between">
          <div className="flex items-center gap-3 text-slate-500">
            <HardDrive className="h-5 w-5" />
            <span className="text-sm">{loading ? 'Chargement…' : 'Statistiques disque indisponibles'}</span>
          </div>
          <Button size="sm" variant="outline" onClick={fetchAll} disabled={loading} data-testid="disk-refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardContent>
      </Card>
    );
  }

  const lvl = LEVEL_STYLES[stats.level] || LEVEL_STYLES.ok;
  const pctSafe = Math.min(stats.pct_used, 100);

  return (
    <Card data-testid="disk-monitoring-widget" className={`overflow-hidden border-slate-200 hover:shadow-lg transition-all`}>
      <CardContent className="p-0">
        <div className="grid grid-cols-1 lg:grid-cols-12">
          {/* Gauge circulaire à gauche */}
          <div className={`lg:col-span-3 bg-gradient-to-br ${lvl.bg} text-white p-6 flex flex-col items-center justify-center text-center`}>
            <div className="flex items-center gap-2 mb-2 opacity-90">
              <HardDrive className="h-4 w-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">Disque /app</span>
            </div>
            <div className={`relative w-28 h-28 rounded-full bg-white/15 ring-4 ${lvl.ring} flex items-center justify-center backdrop-blur-sm`}>
              <div className="text-center">
                <div className="text-3xl font-extrabold leading-none">{Math.round(pctSafe)}%</div>
                <div className="text-[10px] opacity-80 uppercase tracking-wide mt-1">Utilisé</div>
              </div>
            </div>
            <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-white/20 px-2 py-0.5 rounded-full">
              <span className={`w-1.5 h-1.5 rounded-full ${lvl.dot}`} />
              {lvl.label}
            </div>
          </div>

          {/* Stats détaillées */}
          <div className="lg:col-span-9 p-6 space-y-4">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                  <span>Surveillance espace disque</span>
                  {stats.level === 'critical' && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full bg-red-100 text-red-700">
                      <AlertTriangle className="h-3 w-3" /> Action requise
                    </span>
                  )}
                </h3>
                <p className="text-xs text-slate-500 mt-1">
                  {stats.used_gb} GB utilisés sur {stats.total_gb} GB · <span className={`font-semibold ${lvl.text}`}>{stats.free_gb} GB libres</span>
                </p>
              </div>
              <Button size="sm" variant="ghost" onClick={fetchAll} disabled={loading} data-testid="disk-refresh-btn">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            {/* Barre de progression */}
            <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden">
              <div
                className={`h-full bg-gradient-to-r ${lvl.bg} transition-all duration-500`}
                style={{ width: `${pctSafe}%` }}
              />
            </div>

            {/* Détail répartition */}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <FileText className="h-3.5 w-3.5" /> generated_pdfs/
                </div>
                <div className="text-lg font-bold text-slate-900">{stats.pdf_dir.size_mb} MB</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {stats.pdf_dir.count} fichiers · <span className="font-semibold text-amber-700">{stats.pdf_dir.drafts_count} drafts</span>
                </div>
              </div>
              <div className="rounded-lg border border-slate-200 p-3 bg-slate-50">
                <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                  <Database className="h-3.5 w-3.5" /> uploads/ (GED)
                </div>
                <div className="text-lg font-bold text-slate-900">{stats.uploads_dir.size_mb} MB</div>
                <div className="text-[11px] text-slate-500 mt-0.5">Documents membres</div>
              </div>
            </div>

            {/* Bandeau scheduler purge auto */}
            {purgeStatus && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 flex items-center justify-between" data-testid="disk-purge-scheduler-info">
                <div className="text-xs text-emerald-900 leading-relaxed">
                  <span className="font-semibold">Purge auto drafts</span>
                  {' '}— quotidienne à <span className="font-mono">{purgeStatus.schedule}</span>,
                  seuil <span className="font-mono">{purgeStatus.age_days}j</span>
                  {purgeStatus.last_run && (
                    <span className="block text-[11px] text-emerald-700 mt-0.5">
                      Dernière : {new Date(purgeStatus.last_run).toLocaleString('fr-FR')} —
                      {' '}<span className="font-mono">{purgeStatus.last_files_deleted}</span> fichiers,
                      {' '}<span className="font-mono">{purgeStatus.last_bytes_freed_mb} MB</span> libérés
                    </span>
                  )}
                </div>
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase rounded-full ${purgeStatus.running ? 'bg-emerald-200 text-emerald-900' : 'bg-slate-200 text-slate-600'}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${purgeStatus.running ? 'bg-emerald-600 animate-pulse' : 'bg-slate-400'}`} />
                  {purgeStatus.running ? 'Actif' : 'Arrêté'}
                </span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
